// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import { LayoutSizeContext } from 'App';
import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import useLocation from 'hooks/useLocation';
import useRenderTracker from 'hooks/useRenderTracker';
import { useContext, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useGetAlarmsQuery } from 'services/Alarms/Alarms.api';
import { useGetAllActiveAlertsQuery } from 'services/alerts/alerts.api';
import { selectUsername } from 'store/selectors/User/userSelectors';
import DashboardGrid from './components/DashboardGrid/DashboardGrid';
import DashboardSummary from './components/DashboardSummary/DashboardSummary';
import WidgetSelector from './features/WidgetSelector';
import useDashboardLayout from './hooks/useDashboardLayout';
import useDashboardLocationNotice from './hooks/useDashboardLocationNotice';
import useDashboardRowHeight from './hooks/useDashboardRowHeight';
import type { DashboardBreakpoint, DashboardProps } from './types/dashboard.types';
import './assets/Dashboard.scss';

const Dashboard = ({ search, definedWidgetData, accessLevel }: DashboardProps) => {
  useRenderTracker('Dashboard');
  const username = useSelector(selectUsername);
  const perms = usePerms();
  const layout = useContext(LayoutSizeContext);
  const { locations } = useLocation();
  const [breakpoint, setBreakpoint] = useState<DashboardBreakpoint>('xxl');
  const [widgetSelectorOpen, setWidgetSelectorOpen] = useState(false);
  const [showRemoveAllConfirm, setShowRemoveAllConfirm] = useState(false);
  const dashboardGridRef = useRef<HTMLDivElement>(null);

  const canEditWidgets = perms.testPermission([8, 9, 10, 11, 12, 13], ['WidgetsAreEditable']);
  const canOverrideLockedFromRole = perms.testPermission([11, 12, 13], []);
  const showAnalyticHeaders = perms.testPermission([8, 9, 10, 11, 12, 13], ['AnalyticHeaders']);
  const canViewAlerts = perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewAlerts']);
  const canViewAlarms = perms.testPermission([8, 9, 10, 11, 12, 13], ['PendingAlarms']);
  const { data: allActiveAlerts = [] } = useGetAllActiveAlertsQuery(undefined, {
    skip: !canViewAlerts,
  });
  const { data: alarmsData } = useGetAlarmsQuery({ state: 1 }, { skip: !canViewAlarms });

  // Summary polling must not reload the user's in-progress layout edits.
  const weatherAlertCount = allActiveAlerts.filter(
    (alert) => alert.provider === 'Custom Weather',
  ).length;
  const fusionAlertCount = allActiveAlerts.filter((alert) => alert.provider === 'Fusion').length;
  const totalAlarmsCount =
    perms.isFetching || perms.data === null
      ? ''
      : canViewAlarms
        ? (alarmsData?.meta?.resultCount ?? 0)
        : 'Unavailable';

  const {
    list,
    choiceList,
    onListChange,
    persistGridLayoutChanges,
    addWidgets,
    removeWidget,
    removeAllWidgets,
  } = useDashboardLayout({ definedWidgetData, breakpoint, canOverrideLockedFromRole });
  const lockedRoleWidgetCount = list.filter(
    (item) => item.lockedFromRole === true && !canOverrideLockedFromRole,
  ).length;
  const removableWidgetCount = list.length - lockedRoleWidgetCount;
  const dashboardRowHeight = useDashboardRowHeight({
    dashboardGridRef,
    widgetHeight: layout.widgetHeight,
    widgetCount: list.length,
    canEditWidgets,
    showAnalyticHeaders,
    weatherAlertCount,
    fusionAlertCount,
    totalAlarmsCount,
  });
  useDashboardLocationNotice(locations.effectiveLocation.source);

  const removeAllWidgetsAction = (
    <button
      type="button"
      className="bg-transparent border-0 p-0"
      disabled={removableWidgetCount === 0}
      onClick={() => setShowRemoveAllConfirm(true)}
      title={
        lockedRoleWidgetCount > 0
          ? 'Remove all removable widgets (role-locked widgets will remain)'
          : 'Remove all widgets'
      }
      aria-label="Remove all widgets"
      style={{
        color: 'var(--color-font)',
        fontSize: 11,
        lineHeight: 1,
        opacity: removableWidgetCount === 0 ? 0.45 : 0.8,
        textDecoration: 'none',
        cursor: removableWidgetCount === 0 ? 'not-allowed' : 'pointer',
      }}
    >
      Remove All Widgets
    </button>
  );

  return (
    <>
      <Container fluid className="pt-0">
        <p className="fs-6 text-center text-md-start mb-2">
          Welcome back, {username}, glad to see you again!
        </p>
        {showAnalyticHeaders && (
          <DashboardSummary
            {...{ weatherAlertCount, fusionAlertCount, totalAlarmsCount, canViewAlarms }}
          />
        )}
        {canEditWidgets &&
          (showAnalyticHeaders ? (
            <div className="row w-100 gx-3 gy-0 mt-1 mb-0">
              <div
                className="col-12 col-xl-3 offset-xl-9 d-flex justify-content-end align-items-center"
                style={{ minHeight: 14 }}
              >
                {removeAllWidgetsAction}
              </div>
            </div>
          ) : (
            <div
              className="d-flex justify-content-end align-items-center mt-1 mb-0"
              style={{ minHeight: 14 }}
            >
              {removeAllWidgetsAction}
            </div>
          ))}
        <DashboardGrid
          list={list}
          breakpoint={breakpoint}
          onBreakpointChange={setBreakpoint}
          definedWidgetData={definedWidgetData}
          search={search}
          accessLevel={accessLevel}
          canEditWidgets={canEditWidgets}
          dashboardRowHeight={dashboardRowHeight}
          dashboardGridRef={dashboardGridRef}
          onListChange={onListChange}
          removeWidget={removeWidget}
          persistGridLayoutChanges={persistGridLayoutChanges}
          onOpenWidgetSelector={() => setWidgetSelectorOpen(true)}
        />
      </Container>
      {choiceList && widgetSelectorOpen && (
        <WidgetSelector
          widgetData={definedWidgetData}
          userList={choiceList}
          show={widgetSelectorOpen}
          handleShow={() => setWidgetSelectorOpen(false)}
          onAddWidgets={addWidgets}
          onAddWidget={undefined}
        />
      )}
      <ConfirmationModal
        show={showRemoveAllConfirm}
        handleShow={() => setShowRemoveAllConfirm(false)}
        bodyTxt={
          lockedRoleWidgetCount > 0
            ? 'Remove all removable widgets from the dashboard? Role-locked widgets will remain.'
            : 'Remove all widgets from the dashboard?'
        }
        cancelBtnTxt="Cancel"
        confirmBtnTxt="Remove All"
        cancelHandler={() => setShowRemoveAllConfirm(false)}
        confirmHandler={() => {
          removeAllWidgets();
          setShowRemoveAllConfirm(false);
        }}
      />
    </>
  );
};

export default Dashboard;
